import React, { FunctionComponent, useState } from 'react';
import { makeStyles, createStyles, Button, Box, Grid, Theme } from '@material-ui/core';
import { v1 as uuid } from 'uuid';
import FileDropzone from '../../components/FileDropzone';
import UploadedFile from '../UploadedFile';
import { APPLICATION_TYPES } from '../../store/app.constants';
import { DropDownField } from '../../store/modules/application/types';

type FileUploadDialogProps = {
    licenseId?: string;
    personId?: string;
    citationId?: string;
    onClose: Function;
    getAuthenticationToken: Function;
    getFileList: Function;
    toggleFileRequestTrigger?: Function;
};

type UserAddedFileListType = UserAddedFileType[];

type UserAddedFileType = {
    file: File;
    name: string;
    type: string;
    uuid: string;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        uploadDialog: {
            display: 'flex',
            flexGrow: 1,
        },
        filesContainer: {
            margin: theme.spacing(4),
            height: 350,
            'overflow-y': 'scroll',
            'overflow-x': 'clip',
        },
        paragraph: {
            color: '#6E7788',
            fontSize: 24,
            whiteSpace: 'pre-wrap',
            lineHeight: 1.5,
            margin: 0,
        },
        dropzone: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: 'unset',
            minHeight: '100%',
            backgroundColor: '#F5F5F5',
        },
        footer: {
            margin: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            '& button': {
                margin: theme.spacing(1),
            },
        },
    }),
);

const FileUploadDialog: FunctionComponent<FileUploadDialogProps> = (props) => {
    const {
        onClose,
        licenseId,
        getAuthenticationToken,
        getFileList: getFiles,
        personId,
        citationId,
        toggleFileRequestTrigger,
    } = props;

    const [userAddedFiles, setUploadedFiles] = useState<UserAddedFileListType>([]);
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [isUploading, setUploading] = useState<boolean>(false);
    const [isDisabled, setDisabled] = useState<boolean>(true);

    const classes = useStyles();

    // Uploading files through button or drag & drop
    const uploadFiles = (files: File[]) => {
        const mUserAddedFiles: UserAddedFileListType = [...userAddedFiles];

        files.forEach((file) => {
            mUserAddedFiles.push({
                file: file,
                name: file.name,
                type: APPLICATION_TYPES.APPLICATION,
                uuid: uuid(),
            });
        });

        setUploadedFiles(mUserAddedFiles);
        setDisabled(false);
    };

    // Invoked on 'Upload' button click
    const startUpload = () => {
        userAddedFiles.length !== 0 && setUploading(true);
        setDisabled(true);
    };

    // Invoked upon completion of subsequent uploads
    const nextUpload = (index: number) => {
        setActiveIndex(index);
    };

    // When all files have finished uploading, set active index to 0
    const stopUpload = () => {
        setUploading(false);
        setDisabled(true);
    };

    // Delete a file from uploading queue
    const removeFile = (index: number) => {
        if (!isUploading) {
            const newFilesList = userAddedFiles.filter((file) => {
                return index !== userAddedFiles.indexOf(file);
            });

            newFilesList.forEach((file) => {
                file.uuid = uuid();
            });

            setUploadedFiles(newFilesList);
            !newFilesList.length && setDisabled(true);
        }
    };

    // Set file Name from uploading queue
    const setFileName = (index: number, name: string, extension: string) => {
        if (!isUploading) {
            const newFilesList = [...userAddedFiles];
            newFilesList.forEach((file) => {
                if (index === newFilesList.indexOf(file)) {
                    file.name = `${name}${extension}`;
                }
            });
            setUploadedFiles(newFilesList);
        }
    };
    // Set file Type from uploading queue
    const setFileType = (index: number, data: DropDownField | null) => {
        if (!isUploading) {
            const newFilesList = [...userAddedFiles];
            newFilesList.forEach((file) => {
                if (index === newFilesList.indexOf(file)) {
                    file.type = data ? data.value : '';
                }
            });
            setUploadedFiles(newFilesList);
        }
    };

    return (
        <Box className={classes.uploadDialog}>
            <Grid container>
                <Grid item xs={4}>
                    <FileDropzone isUploading={isUploading} uploadFiles={uploadFiles} />
                </Grid>
                <Grid item xs={8}>
                    <Box className={classes.filesContainer}>
                        {userAddedFiles.map((fileInfo, index) => (
                            <UploadedFile
                                key={fileInfo.uuid}
                                file={fileInfo.file}
                                name={fileInfo.name}
                                type={fileInfo.type}
                                fileIndex={index}
                                activeIndex={activeIndex}
                                nextUpload={nextUpload}
                                isUploading={isUploading}
                                filesCount={userAddedFiles.length}
                                stopUpload={stopUpload}
                                removeFile={removeFile}
                                isDisabled={isDisabled}
                                licenseId={licenseId || ''}
                                personId={personId || ''}
                                citationId={citationId || ''}
                                getAuthenticationToken={getAuthenticationToken}
                                getFileList={getFiles}
                                setDocumentTypeForParent={setFileType}
                                setFileNameForParent={setFileName}
                                toggleFileRequestTrigger={toggleFileRequestTrigger}
                            />
                        ))}
                    </Box>
                    <Box className={classes.footer}>
                        <Button
                            disabled={isUploading}
                            disableElevation
                            variant="contained"
                            onClick={() => {
                                onClose(false);
                            }}
                        >
                            Close
                        </Button>
                        <Button
                            disabled={isDisabled}
                            disableElevation
                            color="primary"
                            variant="contained"
                            onClick={startUpload}
                        >
                            Upload
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default FileUploadDialog;
